import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { useTranslation } from "react-i18next";
import MDButton from "components/MDButton";
import {
  AddPresentationInvitesCall,
  GetPresentationShareCall,
  RemovePresentationInviteCall,
  UpdatePresentationShareModeCall,
} from "../../redux/api/presentationShare.api";

const OPTIONS = [
  {
    id: "public",
    labelKey: "pages.presentationShare.publicLabel",
    descKey: "pages.presentationShare.publicDesc",
  },
  {
    id: "university",
    labelKey: "pages.presentationShare.universityLabel",
    descKey: "pages.presentationShare.universityDesc",
  },
  {
    id: "invited",
    labelKey: "pages.presentationShare.invitedLabel",
    descKey: "pages.presentationShare.invitedDesc",
  },
];

function shareUrl(token) {
  if (!token || typeof window === "undefined") return "";
  return `${window.location.origin}/presentation/view/${token}`;
}

export default function SharePresentationDialog({ open, titleId, onClose }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("off");
  const [token, setToken] = useState("");
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !titleId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    GetPresentationShareCall(titleId)
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setError(res.message || t("pages.presentationShare.loadError"));
          return;
        }
        setMode(res.data?.mode || "off");
        setToken(res.data?.token || "");
        setInvites(Array.isArray(res.data?.invites) ? res.data.invites : []);
      })
      .catch(() => {
        if (!cancelled) setError(t("pages.presentationShare.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, titleId, t]);

  const statusKey =
    mode === "public"
      ? "pages.presentationShare.statusPublic"
      : mode === "university"
        ? "pages.presentationShare.statusUniversity"
        : mode === "invited"
          ? "pages.presentationShare.statusInvited"
          : "pages.presentationShare.statusOff";

  const addEmails = async () => {
    const emails = email
      .split(/[\s,;]+/)
      .map((item) => item.trim())
      .filter(Boolean);
    if (!emails.length || !titleId) return;
    setError("");
    const res = await AddPresentationInvitesCall(titleId, emails);
    if (!res.ok) {
      setError(res.message || t("pages.presentationShare.loadError"));
      return;
    }
    setInvites(Array.isArray(res.data?.invites) ? res.data.invites : invites);
    setEmail("");
    setMode("invited");
  };

  const removeEmail = async (inviteId) => {
    const res = await RemovePresentationInviteCall(titleId, inviteId);
    if (!res.ok) {
      setError(res.message || t("pages.presentationShare.loadError"));
      return;
    }
    setInvites((prev) => prev.filter((item) => item.id !== inviteId));
  };

  const save = async (nextMode = mode) => {
    setSaving(true);
    setError("");
    const res = await UpdatePresentationShareModeCall(titleId, nextMode);
    setSaving(false);
    if (!res.ok) {
      setError(res.message || t("pages.presentationShare.loadError"));
      return null;
    }
    const nextToken = res.data?.token || token;
    setMode(res.data?.mode || nextMode);
    if (nextToken) setToken(nextToken);
    return nextToken || null;
  };

  const copy = async () => {
    if (mode === "off") return;
    const nextToken = await save(mode);
    if (!nextToken) return;
    const url = shareUrl(nextToken);
    if (!url) {
      setError(t("pages.presentationShare.copyError"));
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      setError(t("pages.presentationShare.copyError"));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          px: 1,
          py: 0.5,
          boxShadow: "0 24px 60px rgba(15,23,42,0.16)",
        },
      }}
    >
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 650, letterSpacing: "-0.03em", color: "#0f172a" }}>
          {t("pages.presentationShare.title")}
        </Typography>
        <Typography sx={{ mt: 0.5, color: "#64748b", fontSize: 14 }}>
          {t("pages.presentationShare.subtitle")}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Box sx={{ mt: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
            {OPTIONS.map((option) => {
              const selected = mode === option.id;
              return (
                <Box
                  key={option.id}
                  role="radio"
                  aria-checked={selected}
                  tabIndex={0}
                  onClick={() => setMode(option.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setMode(option.id);
                    }
                  }}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                    px: 1.5,
                    py: 1.4,
                    borderRadius: 3,
                    cursor: "pointer",
                    bgcolor: selected ? "rgba(14,165,233,0.08)" : "transparent",
                    transition: "background-color 160ms ease",
                    "&:hover": { bgcolor: selected ? "rgba(14,165,233,0.12)" : "rgba(15,23,42,0.04)" },
                  }}
                >
                  <Box
                    sx={{
                      mt: 0.4,
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: "1.5px solid",
                      borderColor: selected ? "#0ea5e9" : "#cbd5e1",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    {selected ? (
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#0ea5e9" }} />
                    ) : null}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 650, color: "#0f172a" }}>
                      {t(option.labelKey)}
                    </Typography>
                    <Typography sx={{ mt: 0.3, fontSize: 13, lineHeight: 1.45, color: "#64748b" }}>
                      {t(option.descKey)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}

            {mode === "invited" ? (
              <Box sx={{ mt: 1, px: 0.5 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={email}
                    placeholder={t("pages.presentationShare.emailPlaceholder")}
                    onChange={(event) => setEmail(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addEmails();
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: 2.5, bgcolor: "#f8fafc" },
                    }}
                  />
                  <MDButton
                    variant="text"
                    color="info"
                    onClick={addEmails}
                    sx={{ textTransform: "none", fontWeight: 650, minWidth: "auto" }}
                  >
                    {t("pages.presentationShare.add")}
                  </MDButton>
                </Box>
                <Box sx={{ mt: 1.25, display: "flex", flexDirection: "column", gap: 0.25 }}>
                  {invites.map((invite) => (
                    <Box
                      key={invite.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 0.6,
                        px: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: 13.5, color: "#334155" }}>{invite.email}</Typography>
                      <IconButton size="small" aria-label={t("pages.presentationShare.remove")} onClick={() => removeEmail(invite.id)}>
                        <CloseRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : null}
          </Box>
        )}

        <Box
          sx={{
            mt: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.25,
            py: 1,
            borderRadius: 2.5,
            bgcolor: "#f8fafc",
          }}
        >
          <LinkRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
          <Typography
            sx={{
              flex: 1,
              fontSize: 13,
              color: "#475569",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {shareUrl(token) || "—"}
          </Typography>
          <MDButton
            variant="text"
            color="info"
            onClick={copy}
            disabled={mode === "off" || saving || loading}
            sx={{ textTransform: "none", fontWeight: 650, minWidth: "auto" }}
          >
            {copied ? t("pages.presentationShare.copied") : t("pages.presentationShare.copy")}
          </MDButton>
        </Box>

        <Typography sx={{ mt: 1.25, px: 0.5, fontSize: 12.5, color: "#94a3b8" }}>
          {t(statusKey)}
        </Typography>
        {error ? (
          <Typography sx={{ mt: 1, px: 0.5, fontSize: 12.5, color: "#e11d48" }}>{error}</Typography>
        ) : null}

        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {mode !== "off" ? (
            <MDButton
              variant="text"
              color="secondary"
              onClick={async () => {
                const nextToken = await save("off");
                if (nextToken) onClose();
              }}
              sx={{ textTransform: "none", fontWeight: 600, color: "#94a3b8" }}
            >
              {t("pages.presentationShare.stop")}
            </MDButton>
          ) : (
            <span />
          )}
          <MDButton
            variant="contained"
            color="info"
            disabled={saving}
            onClick={onClose}
            sx={{ textTransform: "none", borderRadius: 999, px: 2.5, boxShadow: "none" }}
          >
            {t("pages.presentationShare.done")}
          </MDButton>
        </Box>
      </Box>
    </Dialog>
  );
}
